import { CLE_TYPES, MAX_TABLE_HEIGHT, MIN_TABLE_HEIGHT } from "../constants";
import moment from "moment";
import {
	AllLogsFilterObject,
	CallLogsFilterObject,
	ChannelLogsFilterObject,
	PlatformLogsFilterObject,
	WorkerLogsFilterObject,
} from "../models/TwilioModels";

export const tryGetProp = <T>(defaultValue: T, func: () => T) => {
	try {
		return func();
	} catch {
		return defaultValue;
	}
};

/**
 * Turns PascalCase string into Title Case
 * @param s
 */
export const pascalToTitleCase = (s: string) => {
	return s
		.replace(/([A-Z][a-z])/g, " $1")
		.replace(/([A-Z][A-Z]+)/g, " $1")
		.trim();
};

/**
 * Turns a camelCase string into Title Case
 * @param s
 */
export const camelToTitleCase = (s: string) => {
	return s
		.replace(/([A-Z][a-z])/g, " $1")
		.replace(/([A-Z][A-Z]+)/g, " $1")
		.trim()
		.replace(/^./, (str) => {
			return str.toUpperCase();
		});
};

/**
 * Maps a given number to a CLE_TYPE. in input is null or does not map to a CLE_Type
 * then it maps to CLE_Type[0] or 'none'
 * @param type
 */
export const mapCleType = (type?: number): string => {
	type = type || 0;

	return CLE_TYPES[type] || CLE_TYPES[0];
};

/**
 * returns yes or no based on a given boolean.
 * @param value
 */
export const boolToYesNo = (value?: boolean) => {
	return value ? "Yes" : "No";
};

/**
 * Tries to parse a Json from a given string. If unsuccessful then it will return
 * an object with the original string and the error message
 * @param s json string
 * @param defaultVal optional default value to return in case of error
 */
export const tryJsonParse = (s: string, defaultVal?: any): any => {
	try {
		return JSON.parse(s);
	} catch (error: any) {
		return defaultVal !== undefined
			? defaultVal
			: {
					error: error.message,
					originalValue: s,
				};
	}
};

/**
 * Removes a single property from an object (immutable)
 * @param prop name of the property to remove
 * @param obj object from which to remove the property
 */
export const removeProp = (prop: string, obj: object) => {
	const { [prop]: _, ...rest } = obj as any;
	return rest;
};

export const getUrlWithoutQuery = () => {
	return window.location.origin + window.location.pathname;
};

export const generateTableHeight = (size: number) => {
	return Math.max(Math.min(100 * size, MAX_TABLE_HEIGHT), MIN_TABLE_HEIGHT);
};

export const parsePositiveIntWithMaxLength = (input: string, maxLength: number) => {
	const chars = input.slice(0, maxLength);
	const numberVal = parseInt(chars) || 0;
	return Math.max(numberVal, 0);
};

/**
 * Helper to check if a string includes another string.
 * Can optionally check case sensitive or insensitive
 * @param str string to search within
 * @param search search term
 * @param caseSensitive should the search be case sensitive, optional, defaults to false
 */
export const includes = (str: string, search: string, caseSensitive = false) =>
	caseSensitive ? str.includes(search) : str.toLowerCase().includes(search.toLowerCase());

/**
 * checks if object is empty
 * @param obj object to test
 */
export const isEmptyObject = (obj: object | null) => !obj || Object.keys(obj).length === 0;

//Generate default values.
export const generateAllLogsDefaultFilters = (): AllLogsFilterObject => {
	return {
		eventTypeNames: [
			"taskRouter",
			"platformStatus",
			"runtimeDebugger",
			"browserFunction",
			"cloudFunction",
			"callEvent",
		],
		logLevelValues: [10000, 20000, 30000, 40000, 50000, 50012, 60000],
		searchSid: "",
		correlationToken: "",
		startDate: moment().subtract(1, "hours").toDate(),
		endDate: moment().toDate(),
		selectedEnvironment: "",
		selectedWorker: "Any",
		refreshDateTimespan: 1,
	};
};

export const generateCallLogsDefaultFilters = (): CallLogsFilterObject => {
	return {
		selectedEnvironment: "",
		status: "Any",
		startDate: moment().subtract(1, "hours").toDate(),
		endDate: moment().toDate(),
		searchSid: "",
		direction: "Any",
		duration: 0,
		toNumber: "",
		fromNumber: "",
		refreshDateTimespan: 1,
	};
};

export const generateChannelLogsDefaultFilters = (): ChannelLogsFilterObject => {
	return {
		selectedEnvironment: "",
		selectedService: "",
		status: "Any",
		memberCount: 0,
		messagesCount: 0,
		startDate: moment().subtract(1, "hours").toDate(),
		endDate: moment().toDate(),
		refreshDateTimespan: 1,
	};
};

export const generateWorkerLogsDefaultFilters = (): WorkerLogsFilterObject => {
	return {
		selectedEnvironment: "",
		selectedWorkspace: "",
		searchFriendlyName: "",
		selectedAvailability: "",
	};
};

export const generatePlatformLogsDefaultFilters = (): PlatformLogsFilterObject => {
	return {
		startDate: moment().subtract(1, "hours").toDate(),
		endDate: moment().toDate(),
		refreshDateTimespan: 1,
	};
};

/**
 * Sets the location of the given path in storageObject to the given value.
 * @param path The path to store the value at in local storage.
 * @param value The value to set in local storage.
 * @param storageObject The object to store the value in.
 */
export const setValueByPath = (path: string[], value: any, storageObject: object) => {
	let valueObject = storageObject;
	let i = 0;
	for (i; i < path.length - 1; i++) {
		try {
			if (!valueObject[path[i]]) {
				valueObject[path[i]] = {};
			}
			valueObject = valueObject[path[i]];
		} catch (ex) {
			console.log(ex);
			return storageObject;
		}
	}
	valueObject[path[i]] = value;
	return storageObject;
};

/**
 * Gets the value in storageObject at the given path.
 * @param path The path to the value at in storageObject.
 * @param storageObject The object to search.
 */
export const getValueByPath = (path: string[], storageObject: object) => {
	try {
		return path.reduce((obj: any, pathItem: string) => (obj && obj[pathItem] ? obj[pathItem] : null), storageObject);
	} catch {
		return null;
	}
};

/**
 * Checks is sid string is call sid
 * @param Sid The Twilio Sid.
 */
export const isCallSid = (sid: string) => {
	if (typeof sid === "string") {
		return sid.startsWith("CA");
	}
	return false;
};

/**
 * Checks is sid string is channel sid
 * @param Sid The Twilio Sid.
 */
export const isChannelSid = (sid: string) => {
	if (typeof sid === "string") {
		return sid.startsWith("CH");
	}
	return false;
};

/**
 * gets the type of sid from the twillio sid
 * @param Sid The Twilio Sid.
 */
export const getSidType = (sid: string) => {
	if (isChannelSid(sid)) {
		return "Channel";
	}
	if (isCallSid(sid)) {
		return "Call";
	}
};

/**
 * opens a url in another tab
 * @param url A url string
 */
export const openLinkInNewTab = (url: undefined | string) => {
	if (typeof url === "string") {
		window.open(url);
	}
};
